import React from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { formatMoneyPeriodHeader, getPeriodKey } from "@common/helpers/reports";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { ViewConfig } from "@lib/views";
import { buildClassColumnGrouping } from "@design-system/ViewTable/core/classColumnGrouping";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { makeClassColumnHeaderCell } from "../ClassColumnHeaderCell";
import {
    getColumnPinOptions,
    getLabelColumnMaxSize,
} from "../singleColumnReport";
import { makeCashFlowStatementPeriodCell } from "./CashFlowStatementViewPeriodCell";
import { getCashFlowStatementCellClassName } from "./cashFlowStatementCellClassName";
import { CashFlowStatementViewRow } from "./cashFlowStatementViewTypes";

// ---------------------------------------------------------------------------
// Column sizing constants
// ---------------------------------------------------------------------------

const CFS_LABEL_COLUMN_ID = "label";
const LABEL_MIN_SIZE = 240;
const LABEL_MAX_SIZE = 1200;
const PERIOD_MIN_SIZE = 120;
const PERIOD_MAX_SIZE = 200;

// ---------------------------------------------------------------------------
// Label cell
// ---------------------------------------------------------------------------

const renderLabelCell = React.memo(function CashFlowStatementLabelCell({
    row,
}: CellContext<
    ViewTableFeatures,
    CashFlowStatementViewRow,
    unknown
>): React.ReactElement {
    const { label, isTotal, isSummary } = row.original;
    return (
        <ViewTableCell
            className={getCashFlowStatementCellClassName({
                isTotal,
                isSummary,
            })}
        >
            {label}
        </ViewTableCell>
    );
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function partitionPeriods(periods: ReportPeriodWithLabel[]) {
    const nonTotalPeriods = periods.filter((p) => !p.isTotal);
    const totalPeriods = periods.filter((p) => p.isTotal);
    return { nonTotalPeriods, totalPeriods };
}

// ---------------------------------------------------------------------------
// Column record type
// ---------------------------------------------------------------------------

type CFSColumnRecord = Record<
    string,
    ViewConfig<CashFlowStatementViewRow, string, never>["columns"][string]
>;

// ---------------------------------------------------------------------------
// Public builders
// ---------------------------------------------------------------------------

/**
 * Builds an ephemeral ViewConfig driven by the period list.
 * Uses string as TColumn since column IDs are dynamically derived from periods.
 */
export function buildCashFlowStatementConfig(
    periods: ReportPeriodWithLabel[],
    numberFormat?: NumberFormatValues,
): ViewConfig<CashFlowStatementViewRow, string, never> {
    const classGrouping = buildClassColumnGrouping(periods);

    const columns: CFSColumnRecord = {
        [CFS_LABEL_COLUMN_ID]: {
            name: "Name",
            header: "",
            accessorKey: "label",
            cell: renderLabelCell,
            minSize: LABEL_MIN_SIZE,
            maxSize: getLabelColumnMaxSize({
                periods,
                labelMaxSize: LABEL_MAX_SIZE,
            }),
            immutable: true,
        },
    };

    periods.forEach((period, periodIndex) => {
        const isTotalPeriod = Boolean(period.isTotal);
        const columnId = isTotalPeriod
            ? `total-${getPeriodKey(period)}`
            : `period-${getPeriodKey(period)}`;

        columns[columnId] = {
            name: period.label,
            header: formatMoneyPeriodHeader(period, numberFormat),
            headerCell: makeClassColumnHeaderCell(period, numberFormat),
            accessorKey: "values",
            cell: makeCashFlowStatementPeriodCell({
                period,
                periodIndex,
                allPeriods: periods,
            }),
            minSize: PERIOD_MIN_SIZE,
            maxSize: PERIOD_MAX_SIZE,
            groupPath: classGrouping?.groupPathByColumnId[columnId],
            headerTierDepth: classGrouping?.headerTierDepthByColumnId[columnId],
        };
    });

    return {
        columns,
        groups: classGrouping?.groups,
        nesting: {
            enabled: true,
            expandColumn: CFS_LABEL_COLUMN_ID,
            childrenKey: "children",
        },
    };
}

/**
 * Builds an ephemeral View object (never persisted to DB).
 * Total period columns are pinned right; no selection or bulk-actions.
 */
export function buildCashFlowStatementView(
    periods: ReportPeriodWithLabel[],
): View {
    const { nonTotalPeriods, totalPeriods } = partitionPeriods(periods);

    return {
        type: ViewType.CASH_FLOW_STATEMENT,
        columns: [
            {
                id: CFS_LABEL_COLUMN_ID,
                options: getColumnPinOptions({ periods, pin: "left" }),
            },
            ...nonTotalPeriods.map((p) => ({
                id: `period-${getPeriodKey(p)}`,
            })),
            ...totalPeriods.map((p) => ({
                id: `total-${getPeriodKey(p)}`,
                options: getColumnPinOptions({ periods, pin: "right" }),
            })),
        ],
        filters: {},
        options: {
            selection: false,
            bulkActions: false,
            density: ViewDensity.LG,
        },
    };
}
